import {Alert, Row, Col, Space, Select} from "antd";
import {
  StyledCustomersDataBox,
  StyledDashboardMetricsWrapper,
} from "../../styles/customers/customers";
import {CustomerMetricsSkeleton} from "../customers/customer-details-section/customer-metrics-skeleton";
import {FullPageLoader} from "../full-page-loader";
import {useGetDashboardMetrics} from "../../react-query-hooks/dashboard";
import {useEffect, useState} from "react";

export const DashboardMetrics = () => {
  const getDashboardMetricsQuery = useGetDashboardMetrics();
  const [selectedCurrency, setSelectedCurrency] = useState<string>("");

  useEffect(() => {
    if (getDashboardMetricsQuery.data) {
      const defaultCurrency = getDashboardMetricsQuery.data.currency
        ? getDashboardMetricsQuery.data.currency
        : getDashboardMetricsQuery.data.totalRevenue[0]
        ? getDashboardMetricsQuery.data.totalRevenue[0].currency
        : "";
      setSelectedCurrency(defaultCurrency ?? "");
    }
  }, [getDashboardMetricsQuery.data]);

  if (getDashboardMetricsQuery.isLoading) {
    return <FullPageLoader />;
  }
  if (getDashboardMetricsQuery.isError) {
    const errorMessage =
      getDashboardMetricsQuery.error?.message ?? "Failed to load dashboard metrics";

    return <Alert message="Error" description={errorMessage} type="error" showIcon />;
  }

  if (!getDashboardMetricsQuery.data) {
    return <FullPageLoader />;
  }

  const totalRevenue = getDashboardMetricsQuery.data.totalRevenue;

  function handleTotalRevenue() {
    const currencyExists = totalRevenue.find((c) => c.currency === selectedCurrency);

    if (currencyExists) {
      return currencyExists.amount ? currencyExists.amount : 0;
    } else {
      return 0;
    }
  }

  const currencyOptions = totalRevenue.map((r) => ({
    label: r.currency,
    value: r.currency,
  }));

  return (
    <>
      {getDashboardMetricsQuery.isFetching ? (
        <CustomerMetricsSkeleton />
      ) : (
        <>
          <StyledDashboardMetricsWrapper>
            <div className="show-mobile">
              <div style={{display: "flex", gap: "1.6rem", overflowX: "auto"}}>
                <StyledCustomersDataBox
                  style={{
                    backgroundColor: "#EAE6F3",
                    color: "#272037",
                    borderRadius: "5px",
                    minWidth: "28rem",
                    width: "100%",
                    border: "1.6px solid #43375D",
                  }}
                >
                  <div style={{backgroundColor: "#B3A8CD"}} className="data-circle"></div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <p className="data-box-title">Total revenue </p>
                    {currencyOptions.length > 1 && (
                      <Select
                        size="small"
                        value={selectedCurrency}
                        onChange={setSelectedCurrency}
                        options={currencyOptions}
                        style={{width: "80px"}}
                        onClick={(e) => e.stopPropagation()}
                      />
                    )}
                  </div>
                  <h2 className="data-box-value">
                    {`${selectedCurrency} ${handleTotalRevenue().toLocaleString()}`}
                  </h2>
                </StyledCustomersDataBox>
                <StyledCustomersDataBox
                  style={{
                    backgroundColor: "#E7EDE1",
                    color: "#16350F",
                    borderRadius: "5px",
                    minWidth: "28rem",
                    width: "100%",
                    border: "1.6px solid #355D2C",
                  }}
                >
                  <div style={{backgroundColor: "#B6C2A8"}} className="data-circle"></div>
                  <p className="data-box-title">Total Customers</p>
                  <h2 className="data-box-value">
                    {getDashboardMetricsQuery.data.totalCustomers ?? 0}
                  </h2>
                </StyledCustomersDataBox>
                <StyledCustomersDataBox
                  style={{
                    backgroundColor: "#E5EEE6",
                    color: "#16350F",
                    borderRadius: "5px",
                    minWidth: "28rem",
                    width: "100%",
                    border: "1.6px solid #355D2C",
                  }}
                >
                  <div style={{backgroundColor: "#B3C4B6"}} className="data-circle"></div>
                  <p className="data-box-title">Total Purchases</p>
                  <h2 className="data-box-value">
                    {getDashboardMetricsQuery.data.totalPurchases ?? 0}
                  </h2>
                </StyledCustomersDataBox>
              </div>
            </div>
            <div className="show-desktop">
              <Space size={16} direction="vertical" style={{display: "flex"}}>
                <Row gutter={[32, 20]}>
                  <Col span={24} md={8}>
                    <StyledCustomersDataBox
                      style={{
                        backgroundColor: "#EAE6F3",
                        color: "#272037",
                        borderRadius: "5px",
                        border: "1.6px solid #43375D",
                      }}
                    >
                      <div
                        style={{backgroundColor: "#B3A8CD"}}
                        className="data-circle"
                      ></div>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <p className="data-box-title">Total revenue </p>
                        {currencyOptions.length > 1 && (
                          <Select
                            size="small"
                            value={selectedCurrency}
                            onChange={setSelectedCurrency}
                            options={currencyOptions}
                            style={{width: "80px"}}
                          />
                        )}
                      </div>
                      <h2 className="data-box-value">
                        {`${selectedCurrency} ${handleTotalRevenue().toLocaleString()}`}
                      </h2>
                    </StyledCustomersDataBox>
                  </Col>
                  <Col span={24} md={8}>
                    <StyledCustomersDataBox
                      style={{
                        backgroundColor: "#E7EDE1",
                        color: "#16350F",
                        borderRadius: "5px",
                        border: "1.6px solid #355D2C",
                      }}
                    >
                      <div
                        style={{backgroundColor: "#B6C2A8"}}
                        className="data-circle"
                      ></div>
                      <p className="data-box-title">Total Customers</p>
                      <h2 className="data-box-value">
                        {getDashboardMetricsQuery.data.totalCustomers ?? 0}
                      </h2>
                    </StyledCustomersDataBox>
                  </Col>
                  <Col span={24} md={8}>
                    <StyledCustomersDataBox
                      style={{
                        backgroundColor: "#E5EEE6",
                        color: "#16350F",
                        borderRadius: "5px",
                        border: "1.6px solid #355D2C",
                      }}
                    >
                      <div
                        style={{backgroundColor: "#B3C4B6"}}
                        className="data-circle"
                      ></div>
                      <p className="data-box-title">Total Purchases</p>
                      <h2 className="data-box-value">
                        {getDashboardMetricsQuery.data.totalPurchases ?? 0}
                      </h2>
                    </StyledCustomersDataBox>
                  </Col>
                </Row>
              </Space>
            </div>
          </StyledDashboardMetricsWrapper>
        </>
      )}
    </>
  );
};
